import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, ArrowRight } from 'lucide-react';
import { projectsAPI } from '../api';
import { useLanguage } from '../context/LanguageContext';

const EASE = [0.16, 1, 0.3, 1];
const FALLBACK = 'https://images.unsplash.com/photo-1710131459450-7c384b8be18f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920';

const Hero = () => {
  const [projects, setProjects] = useState([]);
  const { t } = useLanguage();

  useEffect(() => {
    const loadProjects = async () => {
      try {
        const data = await projectsAPI.getFeatured();
        setProjects(data);
      } catch (error) {
        console.error('Error loading projects:', error);
      }
    };
    loadProjects();
  }, []);

  const bg = FALLBACK;

  return (
    <section id="inicio" className="relative min-h-screen flex items-center bg-obsidian overflow-hidden noise-overlay">
      {/* Background Image with cinematic overlays */}
      <div className="absolute inset-0">
        <motion.img
          src={bg}
          alt="Domodomotics"
          className="w-full h-full object-cover"
          initial={{ scale: 1.12, opacity: 0 }}
          animate={{ scale: 1, opacity: 0.85 }}
          transition={{ duration: 1.6, ease: EASE }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/55 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-obsidian/40" />
      </div>

      {/* Ambient glow */}
      <div className="absolute -top-20 right-0 w-[40rem] h-[40rem] bg-brand/15 rounded-full blur-[140px]" />

      {/* Content */}
      <div className="relative z-10 container mx-auto px-6 lg:px-10">
        <div className="max-w-3xl">
          <motion.p
            className="overline mb-6"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.2 }}
          >
            Domodomotics · Estepona
          </motion.p>

          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-extralight text-white leading-[0.95] tracking-tight" aria-label={`${t('hero.title1')} ${t('hero.title2')}`}>
            <motion.span
              className="block"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE, delay: 0.3 }}
            >
              {t('hero.title1')}
            </motion.span>
            <motion.span
              className="block text-gradient-blue font-light"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE, delay: 0.45 }}
            >
              {t('hero.title2')}
            </motion.span>
          </h1>

          <motion.p
            className="mt-8 text-lg md:text-xl text-white/65 font-light max-w-xl leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.6 }}
          >
            {t('hero.subtitle')}
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row gap-4 mt-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.75 }}
          >
            <a
              href="#servicios"
              data-testid="hero-cta-explore"
              className="btn-primary px-8 py-4 rounded-sm text-sm uppercase tracking-wider font-medium inline-flex items-center justify-center gap-2 group"
            >
              {t('hero.exploreServices')}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#proyectos"
              data-testid="hero-cta-projects"
              className="btn-ghost px-8 py-4 rounded-sm text-sm uppercase tracking-wider font-medium inline-flex items-center justify-center"
            >
              {t('hero.viewProjects')}
            </a>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <a
        href="#servicios"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/40 hover:text-white transition-colors animate-bounce"
        aria-label="Scroll"
      >
        <ChevronDown size={28} />
      </a>
    </section>
  );
};

export default Hero;
